import type { VideoCodec } from "./VideoCodec";

/**
 * Minimal AV1 low-overhead bitstream inspection.
 *
 * AV1 has no Annex B framing: a message payload is one temporal unit — a
 * sequence of OBUs (open bitstream units), each with a 1-2 byte header and a
 * LEB128-coded size (AV1 Bitstream Specification section 5.2). This module
 * walks the OBUs for keyframe detection and parses the sequence header for
 * the `av01.*` WebCodecs codec string; the decoder reads everything else
 * from the in-band sequence header.
 */

/**
 * AV1 OBU types (AV1 Bitstream Specification section 5.3.2) relevant here.
 * A plain const object (not an enum) because OBU headers can carry any 4-bit
 * type value, so comparisons happen against plain numbers.
 */
export const ObuType = {
  SequenceHeader: 1,
  TemporalDelimiter: 2,
  FrameHeader: 3,
  Frame: 6,
} as const;

export interface Obu {
  /** `obu_type` from the OBU header. */
  type: number;
  /** The OBU's payload bytes (header and size field excluded). */
  payload: Uint8Array;
}

function readLeb128(
  data: Uint8Array,
  offset: number,
): { value: number; next: number } | undefined {
  let value = 0;
  for (let i = 0; i < 8; i++) {
    if (offset + i >= data.length) {
      return undefined;
    }
    const byte = data[offset + i];
    value |= (byte & 0x7f) * 2 ** (7 * i);
    if ((byte & 0x80) === 0) {
      return { value, next: offset + i + 1 };
    }
  }
  return undefined;
}

/**
 * Split a low-overhead AV1 temporal unit into its OBUs. Walking stops
 * (returning the OBUs found so far) at the first malformed header, truncated
 * size field, or size that overruns the payload. An OBU without a size field
 * extends to the end of the data, as the spec allows for the last OBU of a
 * temporal unit. Returned payloads alias the input buffer.
 */
export function findObus(data: Uint8Array): Obu[] {
  const obus: Obu[] = [];
  let offset = 0;
  while (offset < data.length) {
    const header = data[offset];
    if (header & 0x80) {
      break; // obu_forbidden_bit must be 0
    }
    const type = (header >> 3) & 0x0f;
    const hasExtension = (header & 0x04) !== 0;
    const hasSize = (header & 0x02) !== 0;
    offset += hasExtension ? 2 : 1;
    if (offset > data.length) {
      break;
    }
    if (!hasSize) {
      obus.push({ type, payload: data.subarray(offset) });
      break;
    }
    const size = readLeb128(data, offset);
    if (size === undefined || size.next + size.value > data.length) {
      break;
    }
    obus.push({
      type,
      payload: data.subarray(size.next, size.next + size.value),
    });
    offset = size.next + size.value;
  }
  return obus;
}

/**
 * Whether the AV1 temporal unit contains a KEY_FRAME (clean decoder entry
 * point). In WebCodecs terms, such a unit is an `EncodedVideoChunk` of type
 * "key"; all other units are "delta". When the unit's own sequence header
 * declares `reduced_still_picture_header`, the frame is a keyframe by
 * definition (that mode only encodes intra frames).
 */
export function isKeyframe(data: Uint8Array): boolean {
  let reducedStillPicture = false;
  for (const obu of findObus(data)) {
    if (obu.type === ObuType.SequenceHeader && obu.payload.length >= 1) {
      // seq_profile (3 bits), still_picture (1), reduced_still_picture_header (1)
      reducedStillPicture = (obu.payload[0] & 0x08) !== 0;
    } else if (obu.type === ObuType.Frame || obu.type === ObuType.FrameHeader) {
      if (obu.payload.length < 1) {
        return false;
      }
      if (reducedStillPicture) {
        return true;
      }
      const firstByte = obu.payload[0];
      if (firstByte & 0x80) {
        return false; // show_existing_frame: re-display, not a new frame
      }
      return ((firstByte >> 5) & 0x03) === 0; // frame_type: 0 = KEY_FRAME
    }
  }
  return false;
}

/** Thrown internally when a parse runs past the end of the payload. */
class BitOverrunError extends Error {}

class BitReader {
  #data: Uint8Array;
  #position = 0;

  constructor(data: Uint8Array) {
    this.#data = data;
  }

  public read(count: number): number {
    let value = 0;
    for (let i = 0; i < count; i++) {
      const byteIndex = this.#position >> 3;
      if (byteIndex >= this.#data.length) {
        throw new BitOverrunError();
      }
      value =
        value * 2 + ((this.#data[byteIndex] >> (7 - (this.#position % 8))) & 1);
      this.#position += 1;
    }
    return value;
  }

  /** Variable-length unsigned code (AV1 spec section 4.10.3). */
  public readUvlc(): number {
    let leadingZeros = 0;
    while (this.read(1) === 0) {
      leadingZeros += 1;
      if (leadingZeros > 31) {
        throw new BitOverrunError();
      }
    }
    if (leadingZeros === 0) {
      return 0;
    }
    return this.read(leadingZeros) + 2 ** leadingZeros - 1;
  }
}

interface SequenceHeaderInfo {
  profile: number;
  levelIdx: number;
  tier: number;
  bitDepth: number;
}

/**
 * Parse a sequence-header OBU payload down to the color config (AV1 spec
 * section 5.5). Every field before the bit depth has a computable length, so
 * the walk is deterministic; a truncated payload surfaces as `undefined`.
 */
function parseSequenceHeader(
  payload: Uint8Array,
): SequenceHeaderInfo | undefined {
  const bits = new BitReader(payload);
  try {
    const profile = bits.read(3);
    bits.read(1); // still_picture
    const reducedStillPicture = bits.read(1) === 1;

    let levelIdx: number;
    let tier = 0;
    if (reducedStillPicture) {
      levelIdx = bits.read(5);
    } else {
      let decoderModelInfoPresent = false;
      let bufferDelayLength = 0;
      if (bits.read(1) === 1) {
        // timing_info()
        bits.read(64); // num_units_in_display_tick, time_scale
        if (bits.read(1) === 1) {
          bits.readUvlc(); // num_ticks_per_picture_minus_1
        }
        decoderModelInfoPresent = bits.read(1) === 1;
        if (decoderModelInfoPresent) {
          bufferDelayLength = bits.read(5) + 1;
          bits.read(42); // num_units_in_decoding_tick, removal/presentation lengths
        }
      }
      const initialDisplayDelayPresent = bits.read(1) === 1;
      const operatingPointCount = bits.read(5) + 1;
      levelIdx = 0;
      for (let i = 0; i < operatingPointCount; i++) {
        bits.read(12); // operating_point_idc
        const opLevelIdx = bits.read(5);
        const opTier = opLevelIdx > 7 ? bits.read(1) : 0;
        if (i === 0) {
          levelIdx = opLevelIdx;
          tier = opTier;
        }
        if (decoderModelInfoPresent && bits.read(1) === 1) {
          bits.read(2 * bufferDelayLength + 1); // operating_parameters_info()
        }
        if (initialDisplayDelayPresent && bits.read(1) === 1) {
          bits.read(4); // initial_display_delay_minus_1
        }
      }
    }

    const frameWidthBits = bits.read(4) + 1;
    const frameHeightBits = bits.read(4) + 1;
    bits.read(frameWidthBits + frameHeightBits); // max frame width/height
    if (!reducedStillPicture && bits.read(1) === 1) {
      bits.read(7); // delta/additional frame id lengths
    }
    bits.read(3); // use_128x128_superblock, enable_filter_intra, enable_intra_edge_filter
    if (!reducedStillPicture) {
      bits.read(4); // interintra/masked compound, warped motion, dual filter
      const enableOrderHint = bits.read(1) === 1;
      if (enableOrderHint) {
        bits.read(2); // enable_jnt_comp, enable_ref_frame_mvs
      }
      // seq_force_screen_content_tools: chosen per-frame (2) or fixed here.
      const forceScreenContentTools = bits.read(1) === 1 ? 2 : bits.read(1);
      if (forceScreenContentTools > 0 && bits.read(1) === 0) {
        bits.read(1); // seq_force_integer_mv
      }
      if (enableOrderHint) {
        bits.read(3); // order_hint_bits_minus_1
      }
    }
    bits.read(3); // enable_superres, enable_cdef, enable_restoration

    // color_config: profiles 0/1 are 8- or 10-bit; profile 2 adds 12-bit.
    const highBitdepth = bits.read(1) === 1;
    let bitDepth = highBitdepth ? 10 : 8;
    if (profile === 2 && highBitdepth) {
      bitDepth = bits.read(1) === 1 ? 12 : 10;
    }
    return { profile, levelIdx, tier, bitDepth };
  } catch (err) {
    if (err instanceof BitOverrunError) {
      return undefined;
    }
    throw err;
  }
}

/**
 * Derive the WebCodecs codec string (`av01.P.LLT.DD`, AV1 ISO-BMFF binding)
 * for the temporal unit, or `undefined` when it carries no (usable) sequence
 * header. Keyframes carry their sequence header in-band, so this succeeds
 * exactly where decoding can start.
 */
export function codecStringFromSequenceHeader(
  data: Uint8Array,
): string | undefined {
  const sequenceHeader = findObus(data).find(
    (obu) => obu.type === ObuType.SequenceHeader,
  );
  if (sequenceHeader === undefined) {
    return undefined;
  }
  const info = parseSequenceHeader(sequenceHeader.payload);
  if (info === undefined) {
    return undefined;
  }
  const level = String(info.levelIdx).padStart(2, "0");
  const bitDepth = String(info.bitDepth).padStart(2, "0");
  return `av01.${info.profile}.${level}${info.tier === 0 ? "M" : "H"}.${bitDepth}`;
}

/**
 * The AV1 implementation of the {@link VideoCodec} seam: raw temporal units
 * (no Annex B), keyframes identified by OBU walking, and an `av01.*` codec
 * string parsed from the keyframe's in-band sequence header. Main profile
 * level 3.0 8-bit (`av01.0.04M.08`) is the capability probe because it is
 * the most broadly supported configuration.
 */
export const av1: VideoCodec = {
  formats: ["av1"],
  framing: "raw",
  probeCodecString: "av01.0.04M.08",
  isKeyframe,
  codecStringFromKeyframe: codecStringFromSequenceHeader,
};
